/**
 * @fileoverview BudgetTemplatePage - Budget template configuration page.
 *
 * Page for managing the budget template which defines target spending amounts and
 * income allocation percentages for each category. Now supports monthly templates
 * with month selector.
 *
 * @description This page component:
 * - Displays month selector to view different templates
 * - Displays budget template editor
 * - Shows target budget amounts per category
 * - Shows income allocation percentages per category
 * - Allows adding/removing categories from template
 * - Validates that percentages sum to 100%
 * - Shows read-only view for regular members
 * - Shows "no template" state when month has no template configured
 * - Shows allocation settings (fill negative first, allow over target)
 *
 * Component composition:
 * - BackButton: Return to main page
 * - MonthNavigation: Select which month's template to view/edit
 * - NoCategoriesWarning: Alert when categories needed
 * - BudgetTemplate: Main template editor component
 * - ToggleSwitch: Allocation settings toggles
 *
 * @module pages/BudgetTemplatePage
 */
import { useState } from 'react';
import BudgetTemplate from '../components/BudgetTemplate/BudgetTemplate';
import BackButton from '../components/shared/BackButton';
import NoCategoriesWarning from '../components/shared/NoCategoriesWarning';
import CeasedBudgetBanner from '../components/shared/CeasedBudgetBanner';
import MonthNavigation from '../components/MonthNavigation/MonthNavigation';
import ToggleSwitch from '../components/shared/ToggleSwitch';
import { useBudgetTemplate } from '../hooks/useBudgetTemplate';
import { budgetAPI } from '../services/api';
import styles from './BudgetTemplatePage.module.css';

/**
 * Budget template configuration page.
 *
 * @param {Object} props - Component props
 * @param {Object} props.categoryHook - Category hook
 * @param {Object} props.budgetInfo - Budget info including singleBankMode
 * @param {string} props.userRole - Current user's role
 * @param {Function} props.onBack - Navigate back handler
 * @param {Function} props.onNavigateToCategories - Navigate to categories
 */
function BudgetTemplatePage({
  categoryHook,
  budgetInfo,
  userRole,
  onBack,
  onNavigateToCategories
}) {
  // Month selector state (format: "YYYY-MM")
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );

  // Get budget hook for the selected month
  const budgetHook = useBudgetTemplate(selectedMonth);

  // Allocation settings handlers - use state from budgetHook
  const handleFillNegativeFirstChange = async (checked) => {
    const previousValue = budgetHook.fillNegativeFirst;
    const previousAllowOverTarget = budgetHook.allowOverTarget;

    // Optimistic update
    budgetHook.setFillNegativeFirst(checked);
    // If turning off fillNegativeFirst, also turn off allowOverTarget
    if (!checked) {
      budgetHook.setAllowOverTarget(false);
    }

    try {
      await budgetAPI.updateAllocationSettings(checked, checked ? previousAllowOverTarget : false, selectedMonth);
      // Reload template to get updated state
      budgetHook.reloadBudgetTemplate();
    } catch (error) {
      console.error('Failed to update allocation settings:', error);
      // Revert on error
      budgetHook.setFillNegativeFirst(previousValue);
      budgetHook.setAllowOverTarget(previousAllowOverTarget);
      alert('Failed to update allocation settings. Please try again.');
    }
  };

  const handleAllowOverTargetChange = async (checked) => {
    const previousValue = budgetHook.allowOverTarget;

    // Optimistic update
    budgetHook.setAllowOverTarget(checked);

    try {
      await budgetAPI.updateAllocationSettings(budgetHook.fillNegativeFirst, checked, selectedMonth);
      // Reload template to get updated state
      budgetHook.reloadBudgetTemplate();
    } catch (error) {
      console.error('Failed to update allocation settings:', error);
      // Revert on error
      budgetHook.setAllowOverTarget(previousValue);
      alert('Failed to update allocation settings. Please try again.');
    }
  };

  // Month navigation handlers
  const handlePreviousMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    date.setMonth(date.getMonth() - 1);
    setSelectedMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleNextMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    date.setMonth(date.getMonth() + 1);
    setSelectedMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleGoToCurrentMonth = () => {
    const now = new Date();
    setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  };

  const isCurrentMonth = () => {
    const now = new Date();
    const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return selectedMonth === currentMonthStr;
  };

  const getSelectedMonthDisplay = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  // Filter out user categories in single bank mode
  const displayCategories = budgetInfo?.singleBankMode
    ? categoryHook.categories.filter(cat => cat.userKey === null || cat.userKey === undefined)
    : categoryHook.categories;

  // Handler to restore budget (uncease)
  const handleRestoreBudget = async () => {
    try {
      await budgetAPI.unceaseBudget(budgetInfo.budgetKey);
      // Refresh the page to update budget info
      window.location.reload();
    } catch (error) {
      console.error('Failed to restore budget:', error);
      alert('Failed to restore budget. Please try again.');
    }
  };

  // Show loading state while template is being fetched
  if (budgetHook.isLoading || budgetHook.budgetTemplate === null) {
    return (
      <div className={styles.container}>
        <BackButton onClick={onBack} />
        <h1 className={styles.pageTitle}>Budget Template</h1>
        <p>Loading template...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <BackButton onClick={onBack} />
      <h1 className={styles.pageTitle}>Budget Template</h1>

      {/* Ceased Budget Banner */}
      {budgetInfo?.ceaseDate && (
        <CeasedBudgetBanner
          ceaseDate={budgetInfo.ceaseDate}
          isOwner={userRole === 'owner'}
          onRestore={handleRestoreBudget}
        />
      )}

      <MonthNavigation
        selectedMonthDisplay={getSelectedMonthDisplay()}
        isCurrentMonth={isCurrentMonth()}
        onPreviousMonth={handlePreviousMonth}
        onNextMonth={handleNextMonth}
        onGoToCurrentMonth={handleGoToCurrentMonth}
        hideBalances={true}
      />

      {/* Allocation Settings */}
      <div className={styles.allocationSettings}>
        <h3 className={styles.allocationSettingsTitle}>Allocation Settings</h3>
        <div className={styles.allocationSettingsContent}>
          <ToggleSwitch
            checked={budgetHook.fillNegativeFirst}
            onChange={handleFillNegativeFirstChange}
            label="Fill negative categories first"
            disabled={userRole === 'member' || budgetInfo?.ceaseDate !== null}
          />
          {budgetHook.fillNegativeFirst && (
            <ToggleSwitch
              checked={budgetHook.allowOverTarget}
              onChange={handleAllowOverTargetChange}
              label="Allow over target"
              disabled={userRole === 'member' || budgetInfo?.ceaseDate !== null}
            />
          )}
        </div>
        <p className={styles.allocationSettingsHint}>
          {budgetHook.fillNegativeFirst
            ? budgetHook.allowOverTarget
              ? 'Income will fill negative balances completely, then fill targets.'
              : 'Income will fill negative balances up to target amount, then fill targets.'
            : 'Income will fill categories by target amount in priority order.'}
        </p>
      </div>

      {budgetHook.noTemplate ? (
        <div className={styles.noTemplateMessage}>
          <p>No budget template configured for this month.</p>
          <p className={styles.noTemplateHint}>
            Add categories below to create a template for {selectedMonth}.
          </p>
        </div>
      ) : null}

      <NoCategoriesWarning
        categories={displayCategories}
        onNavigateToCategories={onNavigateToCategories}
      />

      <BudgetTemplate
        budgetTemplate={budgetHook.budgetTemplate}
        showTemplate={true}
        totalTargetBudget={budgetHook.getTotalTargetBudget()}
        totalPercentages={budgetHook.getTotalPercentages()}
        onToggleTemplate={() => {}}
        onUpdateTemplate={budgetHook.updateBudgetTemplate}
        categories={displayCategories}
        onAddCategory={budgetHook.addCategoryToTemplate}
        onRemoveCategory={budgetHook.removeCategoryFromTemplate}
        userRole={userRole}
        readOnly={userRole === 'member' || budgetInfo?.ceaseDate !== null}
      />
    </div>
  );
}

export default BudgetTemplatePage;
